/**
 * Flash promo — Power (integrated H3) generations 60% off, with a
 * pre-sale teaser countdown.
 *
 * Two hardcoded phases:
 *   1. Upcoming (now → H3_PROMO_START_MS): banner shows a teaser with a
 *      live countdown until the sale starts. Credit price is unchanged.
 *   2. Active  (H3_PROMO_START_MS → H3_PROMO_END_MS): banner shows the
 *      live sale countdown and H3 is debited at 60% off.
 *
 * Past H3_PROMO_END_MS every helper becomes a no-op — the banner
 * unmounts and the credit cost reverts automatically. No env vars, no
 * DB, no client state. Delete this file after the sale to clean up.
 *
 * Set 2026-10-02 21:12 UTC. Sale window:
 *   Sat 2026-10-03 00:00 UTC → Sun 2026-10-04 00:00 UTC
 *   (Fri 8 PM EST          → Sat 8 PM EST).
 */
export const H3_PROMO_START_MS = Date.parse("2026-10-03T00:00:00Z");
export const H3_PROMO_END_MS = Date.parse("2026-10-04T00:00:00Z");
export const H3_PROMO_DISCOUNT = 0.6; // 60% off
export const H3_PROMO_DISCOUNT_LABEL = "60% OFF";

export type H3PromoPhase = "upcoming" | "active" | "ended";

export function getH3PromoPhase(now: number = Date.now()): H3PromoPhase {
  if (!Number.isFinite(H3_PROMO_START_MS) || !Number.isFinite(H3_PROMO_END_MS)) {
    return "ended";
  }
  if (now < H3_PROMO_START_MS) return "upcoming";
  if (now < H3_PROMO_END_MS) return "active";
  return "ended";
}

export function isH3PromoActive(now: number = Date.now()): boolean {
  return getH3PromoPhase(now) === "active";
}

/** True while either the teaser or the live sale should be shown. */
export function isH3PromoAnnounced(now: number = Date.now()): boolean {
  const phase = getH3PromoPhase(now);
  return phase === "upcoming" || phase === "active";
}

export function h3PromoMsUntilStart(now: number = Date.now()): number {
  return Math.max(0, H3_PROMO_START_MS - now);
}

export function h3PromoMsUntilEnd(now: number = Date.now()): number {
  return Math.max(0, H3_PROMO_END_MS - now);
}

/**
 * Apply the promo discount to a base credit cost. Only discounts during
 * the active window — teaser-phase generations pay full price. Rounded
 * UP so a real generation is never charged 0 credits.
 */
export function applyH3PromoDiscount(baseCost: number, now: number = Date.now()): number {
  if (!isH3PromoActive(now)) return baseCost;
  return Math.max(1, Math.ceil(baseCost * (1 - H3_PROMO_DISCOUNT)));
}
